import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxButtonComponent,
  OnyxDividerComponent,
  OnyxDropdownDirective,
  OnyxIconComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain, groupBy, isEqual, orderBy } from 'lodash';
import { distinctUntilChanged, map } from 'rxjs';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { LTL_ORDER_POINT_TYPES } from '../../common/constants/ltl-order-point-types';
import { OrderPointCategory } from '../../common/enums/order-point-category';
import { OrderFormService } from '../../common/services/order-form.service';
import type { OrderPointFormGroup } from './order-point-form/order-point-form.component';
import { OrderPointFormComponent } from './order-point-form/order-point-form.component';

export type OrderPointsFormGroup = ReturnType<
  typeof OrderPointsFormComponent.buildForm
>;

export type OrderPointsForm = ReturnType<OrderPointsFormGroup['getRawValue']>;

@Component({
  selector: 'app-order-points-form',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    OnyxIconComponent,
    OnyxButtonComponent,
    OnyxDropdownDirective,
    OrderPointFormComponent,
    OnyxTooltipDirective,
    OnyxDividerComponent,
  ],
  templateUrl: './order-points-form.component.html',
  styleUrl: './order-points-form.component.scss',
})
export class OrderPointsFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly orderFormService = inject(OrderFormService);

  public readonly form = input.required<OrderPointsFormGroup>();
  public readonly parametersValidation = input.required<
    ValidationGroup | undefined
  >();
  public readonly hasRouteEdit = input.required<boolean>();

  protected readonly I18N = 'orders.orderForm.points';

  protected readonly isDedicated = this.orderFormService.isDedicated;
  protected readonly points = this.orderFormService.points;
  protected readonly pointTypes = toSignal(
    this.dictionariesService.getDictionary(DictionaryCode.ORDER_POINT_TYPE),
  );
  protected pointTypesOptions = computed(() => {
    const pointTypes = this.pointTypes();
    if (!pointTypes) return null;

    const { buttons = [], dropdown = [] } = groupBy(pointTypes, ({ value }) =>
      LTL_ORDER_POINT_TYPES.includes(value) ? 'buttons' : 'dropdown',
    );

    return {
      buttons: orderBy(buttons, ({ value }) =>
        LTL_ORDER_POINT_TYPES.indexOf(value),
      ),
      dropdown: dropdown
        .filter((point) => !point.isSystemOnly)
        .map((point) => ({
          ...point,
          value: () => this.addPoint(point),
        })),
    };
  });

  protected pointsDropdownExpanded = signal(false);

  protected pointsDisabledCategories = computed(() => {
    const categoryCount = this.pointsCategoryCount();
    const isDedicated = this.isDedicated();

    let loadingError: string | null = null;
    let unloadingError: string | null = null;

    if (!isDedicated) {
      if (categoryCount[OrderPointCategory.LOADING] >= 1) {
        loadingError = 'disabled';
      }
      if (categoryCount[OrderPointCategory.UNLOADING] >= 1) {
        unloadingError = 'disabled';
      }
    }

    return {
      [OrderPointCategory.LOADING]: loadingError,
      [OrderPointCategory.UNLOADING]: unloadingError,
      [OrderPointCategory.CHECKPOINT]: null,
      [OrderPointCategory.TRANSIT]: !isDedicated ? 'disabled' : null,
      [OrderPointCategory.PICKUP]: null,
    };
  });

  private formValue!: Signal<OrderPointsForm>;
  private pointsCategoryCount = computed(
    () =>
      chain(this.formValue()).countBy('category').value() as Record<
        OrderPointCategory,
        number
      >,
  );

  public ngOnInit(): void {
    this.formValue = toSignal(
      this.form().valueChanges.pipe(
        map(() => this.form().getRawValue()),
        distinctUntilChanged(isEqual),
      ),
      {
        initialValue: this.form().getRawValue(),
        injector: this.injector,
      },
    );
  }

  protected addPoint(point: {
    category: OrderPointCategory;
    value: string;
    includeInOrder: boolean;
    isAlwaysOpen: boolean;
  }): void {
    this.orderFormService.addPoint(point);
  }

  protected movePoint(index: number, direction: -1 | 1): void {
    this.orderFormService.movePoint(index, direction);
  }

  protected removePoint(index: number): void {
    this.orderFormService.removePoint(index);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<OrderPointFormGroup>([]);
  }
}
